/** @format */

import {SnackbarProvider, VariantType, useSnackbar} from 'notistack';
import {useState, useEffect} from 'react';
import {Container, Box, Stack} from '@mui/material';
import {
  AuthenticationDetails,
  CognitoUserPool,
  CognitoUser,
} from 'amazon-cognito-identity-js';
import {Auth} from 'aws-amplify';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import {PrimaryButton} from '../../component/BasicButton';
//Style import
import '../../css/common/common.css';
//API import
import {patchBillingCustomer} from '../../API/API_Collection';

import {useUserInfo} from '../my-site/MainMySite';
import {format} from '../../define';

/**
 * メールアドレス変更
 * @returns JSX.Element
 */
const ChangeEmail = () => {
  const trans = useTranslation().t; //翻訳関数
  //メールアドレスを取得
  const {email} = useUserInfo();

  const [pageFlag, setPageFlag] = useState('formEmail'); //メールアドレス変更フォームと検証コード入力フォーム切り替えフラグ
  const [newEmail, setNewEmail] = useState(''); //変更後のメールアドレス

  return (
    <SnackbarProvider maxSnack={3}>
      <Container fixed className="accountSetting">
        <Stack spacing={3}>
          {/** タイトル */}
          <Box>
            <h1>{trans('ChangeEmail.title')}</h1>
          </Box>
          <Box>
            {/* フラグを判定してメールアドレス変更フォームと検証コード入力フォームを切り替える */}
            {pageFlag === 'formEmail' ? (
              <FormEmail
                nowEmail={email}
                newEmail={newEmail}
                setNewEmail={setNewEmail}
                setPageFlag={setPageFlag}
              />
            ) : (
              <FormVerificationCode newEmail={newEmail} />
            )}
          </Box>
        </Stack>
      </Container>
    </SnackbarProvider>
  );
};

/**
 * メールアドレス変更フォーム
 * @returns JSX
 */
const FormEmail = (props: {
  /** メールアドレス */
  nowEmail: string;
  /** 変更後のメールアドレス */
  newEmail: string;
  /** 変更後のメールアドレス（set関数） */
  setNewEmail: React.Dispatch<React.SetStateAction<string>>;
  /** メールアドレス変更フォームと検証コード入力フォーム切り替えフラグ（set関数） */
  setPageFlag: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const {enqueueSnackbar} = useSnackbar();
  //ユーザ情報取得
  const {userInfo} = useUserInfo();

  const [password, setPassword] = useState(''); //パスワード
  const [reEnterNewEmail, setReEnterNewEmail] = useState(''); //メールアドレス（2回目の入力）
  //prettier-ignore
  const [chgEmailErrorState, setChgEmailErrorState] = useState<'' | 'incorrectEmailPassword' | 'emailAlreadyExists'>(''); //メールアドレス変更リクエスト後のエラー内容
  const [chgEmailBtnDisabled, setChgEmailBtnDisabled] = useState(true); //CHANGE E-MAILボタン非表示フラグ
  const [apiWait, setAPIwait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [passwordError, setPasswordError] = useState(false); //パスワード
  const [newEmailError, setNewEmailError] = useState(false); //変更後のメールアドレス
  const [reEnterNewEmailError, setReEnterNewEmailError] = useState(false); //メールアドレス（2回目の入力）

  //入力データのエラーチェック（メールアドレス変更フォーム）
  useEffect(() => {
    if (
      password === '' ||
      props.newEmail === '' ||
      reEnterNewEmail === '' ||
      passwordError ||
      newEmailError ||
      reEnterNewEmailError
    ) {
      setChgEmailBtnDisabled(true);
    } else {
      setChgEmailBtnDisabled(false);
    }
  }, [
    password,
    props.newEmail,
    reEnterNewEmail,
    passwordError,
    newEmailError,
    reEnterNewEmailError,
  ]);

  /**
   * 変更ボタン押下時の処理
   * @returns
   */
  const onClickReqEmailChg = async () => {
    setChgEmailErrorState('');
    setChgEmailBtnDisabled(true);
    setAPIwait(true);

    //メールアドレスの正規表現
    const regex = format.EMAIL_PATTERN_OK;
    //E-mailアドレスの体裁チェック
    if (
      regex.test(props.newEmail) === false ||
      props.newEmail.search(password) !== -1
    ) {
      setChgEmailErrorState('incorrectEmailPassword');
      setChgEmailBtnDisabled(false);
      setAPIwait(false);
      return;
    }
    if (regex.test(reEnterNewEmail) === false) {
      setChgEmailErrorState('incorrectEmailPassword');
      setChgEmailBtnDisabled(false);
      setAPIwait(false);
      return;
    }
    //E-mailの一致を確認
    if (props.newEmail !== reEnterNewEmail) {
      setChgEmailErrorState('incorrectEmailPassword');
      setChgEmailBtnDisabled(false);
      setAPIwait(false);
      return;
    }

    //ユーザ情報の生成
    const authenticationDetails = new AuthenticationDetails({
      Username: props.nowEmail,
      Password: password,
    });
    const userPool = new CognitoUserPool({
      UserPoolId: process.env.REACT_APP_userPoolId
        ? process.env.REACT_APP_userPoolId
        : '',
      ClientId: process.env.REACT_APP_userPoolWebClientId
        ? process.env.REACT_APP_userPoolWebClientId
        : '',
    });
    const cognitoUser = new CognitoUser({
      Username: props.nowEmail,
      Pool: userPool,
    });

    try {
      //ユーザ情報の取得
      const user = await Auth.currentAuthenticatedUser();
      //メールアドレスの変更処理
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: () => {
          const attributes = {
            /** 新しいメールアドレス */
            'email': props.newEmail,
            /** 古いメールアドレス */
            'custom:validatedEmail': props.nowEmail,
          };
          const clientMetadata = {
            language: userInfo === null ? 'english' : userInfo.language,
          };
          Auth.updateUserAttributes(user, attributes, clientMetadata)
            .then(() => {
              const variant: VariantType = 'success';
              enqueueSnackbar(trans('ChangeEmail.executionResult.successMsg'), {
                variant,
              });
              props.setPageFlag('formVerificationCode');
            })
            .catch((error: any) => {
              const variant: VariantType = 'error';
              if (error.code === 'AliasExistsException') {
                setChgEmailErrorState('emailAlreadyExists');
                enqueueSnackbar(
                  trans('ChangeEmail.executionResult.registered'),
                  {
                    variant,
                  }
                );
              } else {
                setChgEmailErrorState('incorrectEmailPassword');
                enqueueSnackbar(
                  trans('ChangeEmail.executionResult.failureMsg'),
                  {
                    variant,
                  }
                );
              }
              setChgEmailBtnDisabled(false);
            })
            .finally(() => {
              setAPIwait(false);
            });
        },
        //パスワードが一致しない場合のエラー処理
        onFailure(error) {
          setChgEmailErrorState('incorrectEmailPassword');
          setChgEmailBtnDisabled(false);
          setAPIwait(false);
        },
      });
    } catch (error) {
      const variant: VariantType = 'error';
      enqueueSnackbar(trans('ChangeEmail.executionResult.failureMsg'), {
        variant,
      });
      setChgEmailBtnDisabled(false);
      setAPIwait(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box className="accountSetting__text__instruction">
        <h3>{trans('ChangeEmail.instruction.newEmailAddressInputPage')}</h3>
      </Box>
      {/* エラーメッセージ */}
      {chgEmailErrorState !== '' && (
        <Box className="accountSetting__text--error">
          {chgEmailErrorState === 'incorrectEmailPassword'
            ? trans('ChangeEmail.errMsg.inputErr')
            : chgEmailErrorState === 'emailAlreadyExists'
            ? trans('ChangeEmail.errMsg.registered')
            : ''}
        </Box>
      )}
      <Box>
        <Stack>
          {/* 現在のパスワード */}
          <ChkInputTextbox
            setValue={setPassword}
            setError={setPasswordError}
            chkInputTxtOK={format.PASSWORD_PATTERN_OK}
            chkLength={format.PASSWORD_LENGTH_MAX}
            type="password"
            label={trans('ChangeEmail.txtField.label.password')}
            value={password}
            id="mv-inputLoginPassword"
            class="accountSetting__textBox--password"
            helperText=" "
            errHelperText={trans('ChangeEmail.txtField.errHelperTxt')}
            variant="standard"
            autoFocus={true}
          />
          {/* 新しいメールアドレス */}
          <ChkInputTextbox
            setValue={props.setNewEmail}
            setError={setNewEmailError}
            chkInputTxtError={format.BASIC_PATTERN_ERROR}
            chkLength={format.EMAIL_LENGTH_MAX}
            type="text"
            label={trans('ChangeEmail.txtField.label.email')}
            value={props.newEmail}
            id="mv-inputLoginUser"
            class="accountSetting__textBox--email"
            helperText=" "
            errHelperText={trans('ChangeEmail.txtField.errHelperTxt')}
            variant="standard"
          />
          {/* 確認用新しいメールアドレス */}
          <ChkInputTextbox
            setValue={setReEnterNewEmail}
            setError={setReEnterNewEmailError}
            chkInputTxtError={format.BASIC_PATTERN_ERROR}
            chkLength={format.EMAIL_LENGTH_MAX}
            type="text"
            label={trans('ChangeEmail.txtField.label.confirmEmail')}
            value={reEnterNewEmail}
            id="mv-inputLoginReUser"
            class="accountSetting__textBox--email"
            helperText=" "
            errHelperText={trans('ChangeEmail.txtField.errHelperTxt')}
            variant="standard"
          />
        </Stack>
      </Box>
      {/* メールアドレス変更リクエストボタン */}
      <Box>
        <PrimaryButton
          onClick={onClickReqEmailChg}
          buttonText={trans('ChangeEmail.buttonTxt.changeEmail')}
          id="mv-chgEmailBtn"
          disabled={chgEmailBtnDisabled}
          apiWait={apiWait}
        />
      </Box>
    </Stack>
  );
};

/**
 * 検証コード入力フォーム
 * @returns JSX
 */
const FormVerificationCode = (props: {newEmail: string}) => {
  const trans = useTranslation().t; //翻訳関数
  const {enqueueSnackbar} = useSnackbar();

  const [verificationCode, setVerificationCode] = useState(''); //検証コード
  const [verifyBtnDisabled, setVerifyBtnDisabled] = useState(true); //VERIFYボタン非表示フラグ
  //prettier-ignore
  const [chgEmailErrorState, setChgEmailErrorState] = useState<'' | 'incorrectCode'>(''); //検証コードリクエスト後のエラー内容
  const [apiWait, setAPIwait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [verificationCodeError, setVerificationCodeError] = useState(false); //検証コード

  //入力データのエラーチェック
  useEffect(() => {
    if (verificationCode === '' || verificationCodeError) {
      setVerifyBtnDisabled(true);
    } else {
      setVerifyBtnDisabled(false);
    }
  }, [verificationCode, verificationCodeError]);

  /**
   * 検証ボタン押下時の処理
   */
  const onClickVerify = async () => {
    setVerifyBtnDisabled(true);
    setAPIwait(true);
    setChgEmailErrorState('');

    //ユーザ情報の取得
    try {
      const mailPromise = [];
      mailPromise.push(Auth.currentAuthenticatedUser());
      //Stripeに登録されているメールアドレスを変更する
      mailPromise.push(
        patchBillingCustomer({
          'e-mail': props.newEmail,
        })
      );
      const mailResult = await Promise.all(mailPromise);

      if ('result' in mailResult[1] && mailResult[1].result === 'OK') {
        //Stripeのメールアドレス変更が成功した場合のみM-ACCESSのメールアドレスを修正する。
        await Auth.verifyUserAttributeSubmit(
          mailResult[0],
          'email',
          verificationCode
        );
        await Auth.updateUserAttributes(mailResult[0], {
          'email': props.newEmail, //新しいメールアドレス
          'custom:validatedEmail': props.newEmail, //新しいメールアドレス
        });
        const variant: VariantType = 'success';
        enqueueSnackbar(trans('ChangeEmail.executionResult.successMsg'), {
          variant,
        });
        window.location.reload();
      } else {
        //Stripeのメールアドレス変更が失敗
        const variant: VariantType = 'error';
        enqueueSnackbar(trans('ChangeEmail.executionResult.failureMsg'), {
          variant,
        });
        setChgEmailErrorState('incorrectCode');
      }
    } catch (error) {
      const variant: VariantType = 'error';
      enqueueSnackbar(trans('ChangeEmail.executionResult.failureMsg'), {
        variant,
      });
      setChgEmailErrorState('incorrectCode');
    } finally {
      setVerifyBtnDisabled(false);
      setAPIwait(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box className="accountSetting__text__instruction">
        <h3>{trans('ChangeEmail.instruction.verificationCodeInputPage')}</h3>
      </Box>
      {/* エラーメッセージ */}
      {chgEmailErrorState === 'incorrectCode' && (
        <Box className="accountSetting__text--error">
          {trans('ChangeEmail.errMsg.incorrectCode')}
        </Box>
      )}
      <Box>
        {/* 検証コード */}
        <ChkInputTextbox
          setValue={setVerificationCode}
          setError={setVerificationCodeError}
          chkInputTxtOK={format.PASSWORD_PATTERN_OK}
          chkLength={format.PASSWORD_LENGTH_MAX}
          type="password"
          label={trans('ChangeEmail.txtField.label.verificationCode')}
          value={verificationCode}
          id="mv-inputLoginPassword"
          class="accountSetting__textBox--verificationCode"
          helperText=" "
          errHelperText={trans('ChangeEmail.txtField.errHelperTxt')}
          variant="standard"
          autoFocus={true}
        />
      </Box>
      {/* 検証ボタン */}
      <Box>
        <PrimaryButton
          onClick={onClickVerify}
          buttonText={trans('ChangeEmail.buttonTxt.verify')}
          id="mv-verifyBtn"
          disabled={verifyBtnDisabled}
          apiWait={apiWait}
        />
      </Box>
    </Stack>
  );
};

export default ChangeEmail;
